import classnames from 'classnames'
import React from 'react'

import { Button } from '../../main/components/button'
import { getExtendedDialogState } from '../../main/components/dialog'
import { Popover, PopoverTrigger, usePopoverState } from '../../main/components/popover'
import { useElementMeasure } from '../../main/hooks/use-element-measure'
import { ChartSettings, PreventClosingUnsavedChanges, useChartLayoutGlobalModel, useModelState } from '../charts-lib'
import { useOnSettingsClick } from '../shared/hooks/use-on-settings-click'

interface Props {
  isPremium: boolean
}

const BUTTON_POPOVER_GUTTER = 16
const CHART_POPOVER_SPACE_IN_PX = 16
const SETTINGS_POPOVER_ID = 'settings-popover'

function getChartOffsetWhenSettingsIsOpen({
  settingsPopover,
  chartElement,
}: {
  settingsPopover: HTMLElement
  chartElement: HTMLElement
}) {
  const chartsDrawingToolsElement = document.getElementById('charts-drawing-tools') as HTMLDivElement | undefined
  const { ishorizontal } = chartsDrawingToolsElement?.dataset ?? {}
  const drawingToolsWidth = ishorizontal === '1' ? 0 : 95
  const settingsPopoverBoundingClientRect = settingsPopover.getBoundingClientRect()
  const chartElementBoundingClientRect = chartElement.getBoundingClientRect()
  const popoverLeftFromWindowRight = window.innerWidth - settingsPopoverBoundingClientRect.left
  const chartElementRightFromWindowRight = window.innerWidth - chartElementBoundingClientRect.right

  const chartOffset = chartElementRightFromWindowRight - popoverLeftFromWindowRight - CHART_POPOVER_SPACE_IN_PX
  if (Math.abs(chartOffset) + CHART_POPOVER_SPACE_IN_PX + drawingToolsWidth < chartElementBoundingClientRect.left) {
    return `${chartOffset}px`
  }
  return 0
}

type PopoverStylesType = { body?: React.CSSProperties; wrapper?: React.CSSProperties }

function Settings({ isPremium }: Props) {
  const { chartLayoutModel: unwatchedChartLayoutModel } = useChartLayoutGlobalModel()
  const chartLayoutModel = useModelState(unwatchedChartLayoutModel, { watchProperties: ['idea'] })
  const { setElementRef, elementHeight } = useElementMeasure()
  const [popoverStylesState, setPopoverStylesState] = React.useState<PopoverStylesType | null>(null)

  const settingsPopoverState = usePopoverState({ placement: 'bottom-end', unstable_offset: [0, BUTTON_POPOVER_GUTTER] })
  const { isFullyClosed } = getExtendedDialogState(settingsPopoverState)
  const chartElement = document.getElementById('chart')
  const popoverContentElement = document.getElementById(SETTINGS_POPOVER_ID)
  const settingsButton = settingsPopoverState.unstable_referenceRef.current
  const onSettingsClick = useOnSettingsClick({
    isPremium,
    page: 'quote',
    openSettingsCallback: settingsPopoverState.show,
  })

  const popoverStyles = React.useMemo(() => {
    let newPopoverStyles: PopoverStylesType = {}
    if (!settingsButton || !chartElement) {
      setPopoverStylesState(newPopoverStyles)
      return
    }
    const top = settingsButton.offsetTop + settingsButton.offsetHeight
    newPopoverStyles.body = {
      maxHeight: window.innerHeight - top - BUTTON_POPOVER_GUTTER * 2,
      position: 'sticky',
      top: -top,
    }
    if (!popoverContentElement) {
      setPopoverStylesState(newPopoverStyles)
      return
    }
    setElementRef(chartElement as HTMLDivElement)
    newPopoverStyles.wrapper = {
      height: popoverContentElement.offsetHeight < chartElement.clientHeight ? chartElement.clientHeight : 'auto',
    }
    setPopoverStylesState(newPopoverStyles)
  }, [chartElement, settingsButton, popoverContentElement, setElementRef, setPopoverStylesState])

  React.useEffect(() => {
    if (chartElement && popoverContentElement) {
      setPopoverStylesState((prevState) => ({
        ...prevState,
        wrapper: {
          height: popoverContentElement.offsetHeight < chartElement.clientHeight ? chartElement.clientHeight : 'auto',
        },
      }))
    }
  }, [elementHeight, popoverContentElement, chartElement])

  React.useEffect(() => {
    if (chartElement) {
      // transform = translateX even with 0 move element to new CSS stacking context and z-index don't work then what break's select hover,
      // unset fix that issue and do same thing as translateX(0)
      chartElement.style.transform = 'unset'
      chartElement.style.height = 'auto'
      if (settingsPopoverState.visible && popoverContentElement) {
        chartElement.style.transform = `translateX(${getChartOffsetWhenSettingsIsOpen({
          settingsPopover: popoverContentElement,
          chartElement,
        })})`
      }
    }
  }, [chartElement, settingsPopoverState.visible, popoverStyles, popoverContentElement])

  return (
    <>
      <Button
        as={PopoverTrigger}
        size="regular"
        rounding="none"
        className={classnames('btn', 'ml-1 flex items-center !px-3', { 'is-primary': settingsPopoverState.visible })}
        disabled={!chartLayoutModel || chartLayoutModel.idea?.id}
        state={settingsPopoverState}
        onClick={onSettingsClick}
        data-testid="chart-settings-open-btn"
        leftContent="settingsWheel"
      >
        Settings
      </Button>
      <PreventClosingUnsavedChanges onHide={settingsPopoverState.hide}>
        {({ checkForUnsavedChangesAndClose }) => (
          <Popover
            id={SETTINGS_POPOVER_ID}
            aria-label="Chart Settings"
            className="relative z-10 flex max-h-full w-145 max-w-full flex-col"
            state={settingsPopoverState}
            style={popoverStylesState?.body}
            popoverWrapperStyle={popoverStylesState?.wrapper}
            focusOnHide={false}
            hideOnClickOutside={false}
            onOverlayClick={checkForUnsavedChangesAndClose}
          >
            {!isFullyClosed && <ChartSettings onClose={checkForUnsavedChangesAndClose} />}
          </Popover>
        )}
      </PreventClosingUnsavedChanges>
    </>
  )
}

export default Settings
